import React from 'react';
import { LuChevronDown, LuEye, LuEyeOff } from 'react-icons/lu';
import { ELEMENTS_PROPS } from '@/constant/global/global.constant';
import { APP_BG, APP_BORDER, APP_TEXT } from '@/constant/theme/colors.constant';
import { Text } from '@/components/atoms/AppText';
import cx from 'classnames';

interface DisclosureButtonProps {
  /** Whether the panel is currently open */
  open: boolean;
  /** Click handler */
  onClick: () => void;
  /** Label shown when closed. Defaults to "More Details" */
  openLabel?: string;
  /** Label shown when open. Defaults to "Hide" */
  closeLabel?: string;
  /**
   * When true: shows a pulsing dot + count badge to signal hidden content.
   * Pass `false` or omit when there is no content to reveal.
   */
  hasContent?: boolean;
  /** Count badge shown next to the label when closed and hasContent is true */
  count?: number;
  /** Extra Tailwind classes on the wrapper div */
  className?: string;
  /** data-testid forwarded to the button element */
  testId?: string;
}

/**
 * DisclosureButton — pill-style show/hide toggle.
 *
 * States:
 *  - no content  → muted ghost pill
 *  - has content, closed → primary-tinted pill with pulsing dot + count badge
 *  - open        → solid primary pill
 *
 * Usage:
 * ```tsx
 * <DisclosureButton open={open} onClick={() => setOpen(p => !p)} hasContent count={5} />
 * ```
 */
export const DisclosureButton = ({
  open,
  onClick,
  openLabel = ELEMENTS_PROPS.DISCLOSURE_BUTTON.OPEN_LABEL,
  closeLabel = ELEMENTS_PROPS.DISCLOSURE_BUTTON.CLOSE_LABEL,
  hasContent = false,
  count,
  className = '',
  testId,
}: DisclosureButtonProps) => {
  const Icon = open ? LuEyeOff : LuEye;

  return (
    <div className={`flex justify-end ${className}`}>
      <button
        type="button"
        onClick={onClick}
        data-testid={testId ?? ''}
        className={cx(
          'inline-flex items-center gap-1.5 px-3 py-1 rounded-full border text-xs font-semibold',
          'transition-all duration-200 select-none focus:outline-none',
          'focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-1',
          open
            ? 'bg-primary border-primary text-white'
            : hasContent
              ? `border-primary/30 bg-primary/5 dark:bg-primary/10 text-primary`
              : `${APP_BG.base} ${APP_BORDER.soft} ${APP_TEXT.subtle} hover:${APP_TEXT.soft}`,
        )}
      >
        {/* Pulsing dot — only when has content and closed */}
        {hasContent && !open && (
          <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse flex-shrink-0" />
        )}

        <Icon className="w-3 h-3 flex-shrink-0" />

        <Text
          as="span"
          size="xs"
          weight="semibold"
          color="inherit"
          tracking="widest"
          className="uppercase"
        >
          {open ? closeLabel : openLabel}
        </Text>

        {/* Count badge — only when has content, closed, and count provided */}
        {hasContent && !open && count !== undefined && (
          <span className="px-1.5 py-px rounded-full text-[10px] font-bold bg-primary/20 text-primary leading-none">
            {count}
          </span>
        )}

        <LuChevronDown
          className={cx(
            'w-3 h-3 flex-shrink-0 transition-transform duration-200',
            open && 'rotate-180',
          )}
        />
      </button>
    </div>
  );
};
